import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import useAuth from '@contexts/useAuth';
import type { UserProfileData } from '@contexts/AuthContextStore';
import SkipDialog from './components/SkipDialog';
import Customizing from './Customizing';
import { saveUserProfile } from '@api/userProfile';
import { trackAction } from '@utils/datadog';
import { useOnboardingDraft } from './useOnboardingDraft';
import { isValid, type StepErrors, validateStep } from './validation';
import { TOTAL_STEPS } from './options';
import StepAboutYou from './steps/StepAboutYou';
import StepIntent from './steps/StepIntent';
import StepDesignStack from './steps/StepDesignStack';
import StepManufacturingStack from './steps/StepManufacturingStack';
import {
  ActionRow,
  ErrorBanner,
  FormColumn,
  Header,
  Logo,
  Main,
  Page,
  PrimaryButton,
  ProgressArea,
  ProgressFill,
  ProgressInner,
  ProgressMeta,
  ProgressTrack,
  SecondaryButton,
  SkipButton,
  StepCounter,
} from './Onboarding.styled';

/**
 * Post-payment lead-qualification wizard (AIB-6641). 4 steps, a skip button
 * on every step, and a localStorage-backed draft so users don't lose
 * progress on refresh.
 *
 * Layout: a top header with the logo, then a thin progress bar above a step
 * counter and "Skip" link, then the active step centred in a 32rem column
 * with a "Back / Continue" action row. No card
 * wrapper — the form sits directly on the page background.
 *
 * The page is gated by `App.tsx` based on `user.hasCompletedOnboarding`.
 */
const Onboarding: React.FC = () => {
  const intl = useIntl();
  const { user, markOnboardingComplete } = useAuth();

  if (!user) return null;

  return (
    <OnboardingForm
      userId={user.id}
      userToken={user.token}
      markOnboardingComplete={markOnboardingComplete}
      intl={intl}
    />
  );
};

interface OnboardingFormProps {
  userId: string;
  userToken: string;
  markOnboardingComplete: (data: UserProfileData | null, skipped: boolean) => void;
  intl: ReturnType<typeof useIntl>;
}

const OnboardingForm: React.FC<OnboardingFormProps> = ({
  userId,
  userToken,
  markOnboardingComplete,
  intl,
}) => {
  const draft = useOnboardingDraft(userId);
  const [errors, setErrors] = useState<StepErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // When non-null we render the "Setting up your workspace" loader. The
  // pending submission is held in a ref so the Customizing screen can call
  // `markOnboardingComplete` once both the API call and its own animation
  // have finished.
  const [showCustomizing, setShowCustomizing] = useState(false);
  const pendingSubmission = useRef<{
    data: UserProfileData;
    skipped: boolean;
  } | null>(null);

  const requiredMsg = intl.formatMessage({ id: 'onboarding.required' });

  const update = useCallback(
    (patch: Partial<UserProfileData>) => {
      draft.setData((prev) => ({ ...prev, ...patch }));
      // Clear errors for fields the user has just edited so the inline error
      // disappears as soon as they start typing.
      setErrors((prevErrors) => {
        const next = { ...prevErrors };
        for (const key of Object.keys(patch) as Array<keyof UserProfileData>) {
          delete next[key];
        }
        return next;
      });
    },
    [draft],
  );

  const finish = useCallback(
    async (data: UserProfileData, skipped: boolean) => {
      setSubmitting(true);
      setSubmitError(null);
      // Show the "Setting up your workspace" screen straight away; the API
      // call runs in parallel. The Customizing screen waits for both its
      // animation and the API to settle before calling `handleCustomizingDone`.
      setShowCustomizing(true);
      try {
        await saveUserProfile(userToken, data, skipped);
        pendingSubmission.current = { data, skipped };
      } catch (e) {
        const message =
          e instanceof Error
            ? e.message
            : intl.formatMessage({ id: 'onboarding.error.saveFailed' });
        setSubmitError(message);
        // Drop back to the form so the user can see the error and retry.
        setShowCustomizing(false);
      } finally {
        setSubmitting(false);
      }
    },
    [userToken, intl],
  );

  const navigate = useNavigate();

  const handleCustomizingDone = useCallback(() => {
    const submission = pendingSubmission.current;
    if (!submission) return;
    pendingSubmission.current = null;
    // Capture the user's final step BEFORE resetting the draft so analytics
    // see the real value (e.g. step 3 = skipped on the manufacturing stack)
    // rather than the post-reset zero. The closure already captures the
    // pre-reset `draft` reference, but spelling it out keeps intent obvious.
    const stepAtCompletion = draft.step;
    markOnboardingComplete(
      submission.skipped ? null : submission.data,
      submission.skipped,
    );
    draft.reset();
    trackAction('os_onboarding_completed', {
      skipped: submission.skipped,
      step: stepAtCompletion,
    });
    // Push the user off `/onboarding` so `App.tsx`'s gate falls through to
    // `AuthenticatedRoutes`. Without this `OnboardingRoutes` would re-render
    // `<Onboarding />` via its `*` catch-all and the user would get stuck.
    navigate('/', { replace: true });
  }, [draft, markOnboardingComplete, navigate]);

  const handleNext = useCallback(() => {
    const stepErrors = validateStep(draft.step, draft.data, requiredMsg);
    if (!isValid(stepErrors)) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (draft.step === TOTAL_STEPS - 1) {
      // `finish` swallows its own errors into local state; the no-op
      // `.catch` is just here to satisfy `no-floating-promises` without
      // resorting to a `void` operator (Sonar S6544).
      finish(draft.data, false).catch(() => {});
      return;
    }
    draft.setStep(draft.step + 1);
  }, [draft, requiredMsg, finish]);

  const handleBack = useCallback(() => {
    setErrors({});
    if (draft.step > 0) draft.setStep(draft.step - 1);
  }, [draft]);

  const handleSkip = useCallback(() => {
    // See note on `handleNext` — `.catch(() => {})` over `void` to keep
    // Sonar happy while preserving fire-and-forget semantics.
    finish({}, true).catch(() => {});
    setShowSkipConfirm(false);
  }, [finish]);

  const isLastStep = draft.step === TOTAL_STEPS - 1;
  const progress = ((draft.step + 1) / TOTAL_STEPS) * 100;

  // Block the Continue/Finish button until the current step's required fields
  // are populated. Re-runs on every render so the button enables itself
  // immediately when the user types the last missing answer.
  const currentStepValid = useMemo(
    () => isValid(validateStep(draft.step, draft.data, requiredMsg)),
    [draft.step, draft.data, requiredMsg],
  );

  if (showCustomizing) {
    return (
      <Customizing
        apiPending={submitting}
        onComplete={handleCustomizingDone}
      />
    );
  }

  return (
    <Page data-testid="onboarding-page">
      <Header>
        <Logo src="/img/icons/logo.svg" alt="Aibuild" />
      </Header>

      <ProgressArea>
        <ProgressInner>
          <ProgressTrack
            role="progressbar"
            aria-valuenow={draft.step + 1}
            aria-valuemin={1}
            aria-valuemax={TOTAL_STEPS}
          >
            <ProgressFill $value={progress} />
          </ProgressTrack>
          <ProgressMeta>
            <StepCounter data-testid="onboarding-step-counter">
              {intl.formatMessage(
                { id: 'onboarding.stepCounter' },
                { current: draft.step + 1, total: TOTAL_STEPS },
              )}
            </StepCounter>
          </ProgressMeta>
          <SkipButton
            type="button"
            data-testid="onboarding-skip"
            onClick={() => setShowSkipConfirm(true)}
            disabled={submitting}
          >
            {intl.formatMessage({ id: 'onboarding.skip' })}
          </SkipButton>
        </ProgressInner>
      </ProgressArea>

      <Main>
        <FormColumn key={draft.step}>
          {renderStep(draft.step, draft.data, errors, update)}

          {submitError && (
            <ErrorBanner role="alert" data-testid="onboarding-submit-error">
              {submitError}
            </ErrorBanner>
          )}

          <ActionRow>
            {draft.step > 0 && (
              <SecondaryButton
                type="button"
                data-testid="onboarding-back"
                onClick={handleBack}
                disabled={submitting}
              >
                {intl.formatMessage({ id: 'onboarding.back' })}
              </SecondaryButton>
            )}
            <PrimaryButton
              type="button"
              data-testid="onboarding-next"
              onClick={handleNext}
              disabled={submitting || !currentStepValid}
            >
              {intl.formatMessage({
                id: isLastStep ? 'onboarding.finish' : 'onboarding.continue',
              })}
            </PrimaryButton>
          </ActionRow>
        </FormColumn>
      </Main>

      <SkipDialog
        show={showSkipConfirm}
        loading={submitting}
        onContinue={() => setShowSkipConfirm(false)}
        onSkip={handleSkip}
      />
    </Page>
  );
};

function renderStep(
  step: number,
  data: UserProfileData,
  errors: StepErrors,
  update: (patch: Partial<UserProfileData>) => void,
) {
  switch (step) {
    case 0:
      return <StepAboutYou data={data} errors={errors} update={update} />;
    case 1:
      return <StepIntent data={data} errors={errors} update={update} />;
    case 2:
      return <StepDesignStack data={data} errors={errors} update={update} />;
    case 3:
    default:
      return (
        <StepManufacturingStack data={data} errors={errors} update={update} />
      );
  }
}

export default Onboarding;
